import type { ReactNode } from "react";
import "./HomeMenu.css";

/**
 * The small round icon buttons under Home's three doors: ranks, ninjas,
 * shop, switch player and parents. Each has a short caption and a full
 * accessible name, so screen readers and the e2e specs still see
 * "See all ranks", "My ninjas" and so on.
 */

export type MenuIcon = "ranks" | "ninjas" | "shop" | "switch" | "parents";

interface Item {
  icon: MenuIcon;
  /** Accessible name (the old link text). */
  label: string;
  /** Short caption under the icon. */
  caption: string;
  onClick: () => void;
}

interface Props {
  items: readonly Item[];
}

export function HomeMenu({ items }: Props) {
  return (
    <nav className="home-menu" aria-label="More">
      {items.map((item) => (
        <button
          key={item.icon}
          type="button"
          className={`menu-btn menu-${item.icon}`}
          onClick={item.onClick}
          aria-label={item.label}
        >
          <span className="menu-icon" aria-hidden="true">
            {ICONS[item.icon]}
          </span>
          <span className="menu-caption" aria-hidden="true">
            {item.caption}
          </span>
        </button>
      ))}
    </nav>
  );
}

const svg = (children: ReactNode) => (
  <svg viewBox="0 0 24 24" width="28" height="28" focusable="false">
    {children}
  </svg>
);

const ICONS: Record<MenuIcon, ReactNode> = {
  // A rolled scroll: nine of them make the game.
  ranks: svg(
    <g>
      <rect x="7" y="5" width="10" height="14" fill="currentColor" />
      <rect x="4" y="2.5" width="16" height="5" rx="2.5" fill="currentColor" />
      <rect x="4" y="16.5" width="16" height="5" rx="2.5" fill="currentColor" />
      <path
        d="M9.5 10.5h5M9.5 13.5h5"
        stroke="var(--paper)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </g>,
  ),
  // A ninja head: hood, headband and two eyes.
  ninjas: svg(
    <g>
      <circle cx="12" cy="12" r="9" fill="currentColor" />
      <rect x="4" y="9" width="16" height="5" rx="2.5" fill="var(--paper)" />
      <circle cx="9.3" cy="11.5" r="1.3" fill="currentColor" />
      <circle cx="14.7" cy="11.5" r="1.3" fill="currentColor" />
      <path d="M19.5 9.5l3-1.5-1.5 3.2" fill="currentColor" />
    </g>,
  ),
  // A coin pouch.
  shop: svg(
    <g>
      <path
        d="M9 7.5h6l2.6 3.2A7 7 0 0 1 19 15.2C19 18.7 15.9 21 12 21s-7-2.3-7-5.8a7 7 0 0 1 1.4-4.5Z"
        fill="currentColor"
      />
      <path d="M9 7.5 8 4.5h8l-1 3" fill="currentColor" />
      <circle cx="12" cy="14.8" r="3" fill="var(--paper)" />
      <path
        d="M12 13.2v3.2M10.8 14h2.4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </g>,
  ),
  // Two arrows swapping.
  switch: svg(
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 9h13" />
      <path d="M13.5 5.5 17 9l-3.5 3.5" />
      <path d="M20 15H7" />
      <path d="M10.5 11.5 7 15l3.5 3.5" />
    </g>,
  ),
  // A padlock: grown-ups only.
  parents: svg(
    <g>
      <path
        d="M8 10V8a4 4 0 0 1 8 0v2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <rect x="5" y="10" width="14" height="11" rx="3" fill="currentColor" />
      <circle cx="12" cy="15" r="1.6" fill="var(--paper)" />
      <path d="M12 15.5v2.5" stroke="var(--paper)" strokeWidth="1.6" strokeLinecap="round" />
    </g>,
  ),
};
